import React from 'react';

export interface BreadcrumbItem {
  label: string;
  href?: string;
}

export interface PageHeaderProps {
  title: React.ReactNode;
  description?: React.ReactNode;
  badge?: React.ReactNode;
  actions?: React.ReactNode;
  breadcrumbs?: BreadcrumbItem[] | React.ReactNode;
  className?: string;
}

export function PageHeader({
  title,
  description,
  badge,
  actions,
  breadcrumbs,
  className = '',
}: PageHeaderProps) {
  const renderBreadcrumbs = () => {
    if (!breadcrumbs) return null;
    if (Array.isArray(breadcrumbs)) {
      return (
        <nav className="flex items-center gap-1.5 text-xs text-gray-500 mb-1" aria-label="Breadcrumbs">
          {breadcrumbs.map((item: BreadcrumbItem, idx: number) => {
            const isLast = idx === breadcrumbs.length - 1;
            return (
              <React.Fragment key={idx}>
                {idx > 0 && <span className="text-gray-300">/</span>}
                {item.href && !isLast ? (
                  <a
                    href={item.href}
                    className="hover:text-gray-900 transition-colors"
                  >
                    {item.label}
                  </a>
                ) : (
                  <span className={isLast ? 'text-gray-700 font-medium' : ''}>
                    {item.label}
                  </span>
                )}
              </React.Fragment>
            );
          })}
        </nav>
      );
    }
    return <div className="text-xs text-gray-500 mb-1">{breadcrumbs}</div>;
  };

  return (
    <div className={`flex flex-col gap-3 pb-5 border-b border-gray-200/80 mb-6 ${className}`}>
      {renderBreadcrumbs()}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2.5 flex-wrap">
            {typeof title === 'string' ? (
              <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-gray-900 truncate">
                {title}
              </h1>
            ) : (
              <div className="text-xl sm:text-2xl font-bold tracking-tight text-gray-900">
                {title}
              </div>
            )}
            {badge && <div className="inline-flex items-center">{badge}</div>}
          </div>
          {description && (
            <div className="mt-1 text-xs sm:text-sm text-gray-500 leading-relaxed max-w-4xl">
              {description}
            </div>
          )}
        </div>
        {actions && (
          <div className="flex items-center gap-2.5 flex-wrap shrink-0">
            {actions}
          </div>
        )}
      </div>
    </div>
  );
}

export default PageHeader;
